import { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../baseurl";
import { useToast } from "../admincomponents/AdminToast";
export default function Products() {
  const toast = useToast();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const emptyForm = {
    title: "",
    price: "",
    description: "",
    image: null,
  };
  const [formData, setFormData] = useState(emptyForm);
  const [imagePreview, setImagePreview] = useState(null);

  const token = localStorage.getItem("adminToken");
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE_URL}/items`);
      setProducts(data.items);
    } catch (error) {
      toast({
        variant: "error",
        title: "Couldn't load products",
        description: error.response?.data?.message || "Please refresh and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
   
  }, []);

  const openAddModal = () => {
    setEditingProduct(null);
    setFormData(emptyForm);
    setImagePreview(null);
    setIsModalOpen(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      price: product.price,
      description: product.description,
      image: null,
    });
    setImagePreview(product.image ? `${BASE_URL.replace("/api", "")}${product.image}` : null);
    setIsModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const allowedTypes = ["image/jpeg", "image/png", "image/webp"];

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    if (!allowedTypes.includes(file.type)) {
      toast({
        variant: "error",
        title: "Unsupported file type",
        description: "Please upload a .jpg, .jpeg, .png, or .webp image.",
      });
      e.target.value = ""; 
      return;
    }
  
    setFormData((prev) => ({ ...prev, image: file }));
    setImagePreview(URL.createObjectURL(file));
  };
  

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingProduct && !formData.image) {
      toast({
        variant: "error",
        title: "Image required",
        description: "Please select an image for this product.",
      });
      return;
    }

    const payload = new FormData();
    payload.append("title", formData.title);
    payload.append("description", formData.description);
    payload.append("price", formData.price);
    if (formData.image) payload.append("image", formData.image);

    setSubmitting(true);
    try {
      if (editingProduct) {
        await axios.put(`${BASE_URL}/items/${editingProduct._id}`, payload, {
          headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
        });
        toast({
          variant: "success",
          title: "Product updated",
          description: `${formData.title} has been saved.`,
        });
      } else {
        await axios.post(`${BASE_URL}/items`, payload, {
          headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
        });
        toast({
          variant: "success",
          title: "Product created",
          description: `${formData.title} is now in your catalog.`,
        });
      }

      setIsModalOpen(false);
      fetchItems();
    } catch (error) {
      const message = error.response?.data?.message || "Something went wrong, please try again.";
      toast({
        variant: "error",
        title: editingProduct ? "Update failed" : "Creation failed",
        description: message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${BASE_URL}/items/${deleteTarget._id}`, { headers: authHeaders });
      toast({
        variant: "success",
        title: "Product deleted",
        description: `${deleteTarget.title} has been removed.`,
      });
      setDeleteTarget(null);
      fetchItems();
    } catch (error) {
      toast({
        variant: "error",
        title: "Delete failed",
        description: error.response?.data?.message || "Something went wrong, please try again.",
      });
    }
  };

  const filteredProducts = products.filter((product) =>
    product.title?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-gray-800 font-semibold text-2xl">Products</h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage your store's product catalog
          </p>
        </div>

        <button
          onClick={openAddModal}
          className="flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-medium text-sm rounded-md px-5 py-2.5 transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Add Product
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
            <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-white border border-gray-300 rounded-md pl-9 pr-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500"
          />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left">
                <th className="px-6 py-4 text-gray-500 font-medium">Product</th>
                <th className="px-6 py-4 text-gray-500 font-medium">Price</th>
                <th className="px-6 py-4 text-gray-500 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={3} className="px-6 py-10 text-center text-gray-400">
                    Loading products...
                  </td>
                </tr>
              )}

              {!loading &&
                filteredProducts.map((product) => (
                  <tr
                    key={product._id}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {product.image ? (
                            <img
                              src={`${BASE_URL.replace("/api", "")}${product.image}`}
                              alt={product.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-gray-300" xmlns="http://www.w3.org/2000/svg">
                              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
                              <circle cx="9" cy="9" r="1.5" fill="currentColor" />
                              <path d="M21 15l-5-5-9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                        <p className="text-gray-800 font-medium">{product.title}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-800 font-medium">
                      ${Number(product.price).toFixed(2)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-4">
                        <button
                          onClick={() => openEditModal(product)}
                          className="text-teal-600 hover:text-teal-700 font-medium text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteTarget(product)}
                          className="text-red-500 hover:text-red-600 font-medium text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

              {!loading && filteredProducts.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-6 py-10 text-center text-gray-400">
                    No products match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setIsModalOpen(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
                <h2 className="text-gray-800 font-semibold text-lg">
                  {editingProduct ? "Edit Product" : "Add Product"}
                </h2>
                <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-700">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="px-6 py-6 flex flex-col gap-5">
                <div>
                  <label className="block text-gray-600 text-sm font-medium mb-2">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-600 text-sm font-medium mb-2">Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-600 text-sm font-medium mb-2">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    required
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500 resize-y"
                  />
                </div>

                <div>
                  <label className="block text-gray-600 text-sm font-medium mb-2">
                    Image {editingProduct && <span className="text-gray-400 font-normal">(leave blank to keep current)</span>}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-teal-50 file:text-teal-600 file:text-sm file:font-medium hover:file:bg-teal-100"
                  />
                  {imagePreview && (
                    <img src={imagePreview} alt="Preview" className="mt-3 w-24 h-24 object-cover rounded-md border border-gray-200" />
                  )}
                </div>

                <div className="flex items-center gap-3 mt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-md py-2.5 transition-colors"
                  >
                    {submitting
                      ? editingProduct
                        ? "Saving..."
                        : "Adding..."
                      : editingProduct
                      ? "Save Changes"
                      : "Add Product"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 border border-gray-300 text-gray-600 font-medium text-sm rounded-md py-2.5 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {deleteTarget && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setDeleteTarget(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-sm p-6">
              <h3 className="text-gray-800 font-semibold text-lg">Delete Product</h3>
              <p className="text-gray-500 text-sm mt-2">
                Are you sure you want to delete "{deleteTarget.title}"? This can't be undone.
              </p>

              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={confirmDelete}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold text-sm rounded-md py-2.5 transition-colors"
                >
                  Delete
                </button>
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="flex-1 border border-gray-300 text-gray-600 font-medium text-sm rounded-md py-2.5 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}